import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  CircularProgress
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { reservationService } from '../../services/reservationService';

interface ReservationValidationProps {
  reservationId: string;
  email: string;
  onValidationComplete: (success: boolean) => void;
}

const ReservationValidation: React.FC<ReservationValidationProps> = ({
  reservationId,
  email,
  onValidationComplete
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!code) {
      setError(t('reservation:errors.validationCodeRequired'));
      return;
    }
    
    setLoading(true);
    
    try {
      const isValid = await reservationService.validateReservation(reservationId, code);
      
      if (isValid) {
        onValidationComplete(true);
      } else {
        setError(t('reservation:errors.invalidValidationCode'));
      }
    } catch (error) {
      console.error('Error validating reservation:', error);
      setError(error instanceof Error ? error.message : String(error));
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    setResendSuccess(false);
    setError(null);
    
    try {
      await reservationService.generateValidationCode(reservationId, email);
      setResendSuccess(true);
    } catch (error) {
      console.error('Error resending validation code:', error);
      setError(error instanceof Error ? error.message : String(error));
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        {t('reservation:publicPage.validation.title')}
      </Typography>
      
      <Typography paragraph>
        {t('reservation:publicPage.validation.instructions')}
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {resendSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('reservation:publicPage.validation.resendSuccess')}
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
        <TextField
          label={t('reservation:publicPage.validation.codePlaceholder')}
          fullWidth
          value={code}
          onChange={(e) => setCode(e.target.value)}
          disabled={loading}
          required
          sx={{ mb: 2 }}
        />
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : undefined}
          >
            {loading ? t('reservation:common.processing') : t('reservation:publicPage.validation.submit')}
          </Button>
          
          <Button
            variant="outlined"
            onClick={handleResendCode}
            disabled={resendLoading}
            startIcon={resendLoading ? <CircularProgress size={20} /> : undefined}
          >
            {resendLoading ? t('reservation:common.processing') : t('reservation:publicPage.validation.resend')}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default ReservationValidation;
